import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Shield, Check, Crosshair } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export function HeroSection() {
  return <section className="relative min-h-screen flex items-center justify-start overflow-hidden bg-[#0a0f1a] font-sans selection:bg-teal-500/30" data-id="element-40">
      {/* Background Image */}
      <div className="absolute inset-0 z-0" data-id="element-41">
        <motion.img initial={{
        scale: 1.05,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 0.6
      }} transition={{
        duration: 2,
        ease: 'easeOut'
      }} src="https://cdn.magicpatterns.com/uploads/c64f2NuXqtbhbSKtAgZ12z/portada_facebook.png" alt="Executive office with emergency gear" className="w-full h-full object-cover object-right md:object-center" data-id="element-42" />
      </div>

      {/* Gradient Overlays for Text Readability & Cinematic Feel */}
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-[#0a0f1a] via-[#0a0f1a]/90 to-transparent md:w-3/4" data-id="element-43" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#0a0f1a] via-transparent to-transparent opacity-80" data-id="element-44" />

      {/* Vignette */}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,transparent_0%,#0a0f1a_120%)] pointer-events-none" data-id="element-45" />

      {/* Tactical Grid Overlay */}
      <div className="absolute inset-0 z-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: `linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)`,
      backgroundSize: '40px 40px'
    }} data-id="element-46" />

      {/* Scanline Overlay */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-20 bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.5)_50%)] bg-[length:100%_4px]" data-id="element-47" />

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32" data-id="element-48">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-3xl" data-id="element-49">
          {/* Tactical Header / Kicker */}
          <motion.div variants={itemVariants} className="flex items-center gap-4 mb-8" data-id="element-50">
            <div className="flex items-center justify-center w-8 h-8 rounded border border-teal-500/30 bg-teal-500/10" data-id="element-51">
              <Crosshair className="w-4 h-4 text-teal-400" data-id="element-52" />
            </div>
            <div className="h-[1px] w-12 bg-gradient-to-r from-teal-500 to-transparent" data-id="element-53" />
            <span className="text-teal-400 font-mono text-xs md:text-sm tracking-[0.25em] uppercase font-semibold" data-id="element-54">
              Classified Briefing
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 variants={itemVariants} className="flex flex-col gap-2 mb-6" data-id="element-55">
            <span className="text-2xl md:text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 tracking-[0.15em] uppercase" data-id="element-56">
              The Sentinel Protocol:
            </span>
            <span className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.1] tracking-tight" data-id="element-57">
              Professional Family Resilience.
            </span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.div variants={itemVariants} className="relative mb-10" data-id="element-58">
            <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-teal-500 to-transparent" data-id="element-59" />
            <p className="text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed pl-6 max-w-2xl font-light" data-id="element-60">
              The smart logistics manual to organize your home, secure your
              resources, and protect your family's peace of mind.
            </p>
          </motion.div>

          {/* Target Line / Features */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-8 mb-12 p-4 rounded-lg bg-white/[0.02] border border-white/[0.05] backdrop-blur-sm" data-id="element-61">
            <div className="flex items-center gap-3" data-id="element-62">
              <Shield className="w-5 h-5 text-teal-500" data-id="element-63" />
              <span className="text-sm md:text-base text-gray-300 font-medium" data-id="element-64">
                Designed for modern urban apartments and homes.
              </span>
            </div>
            <div className="flex items-center gap-6" data-id="element-65">
              <span className="flex items-center gap-2 text-sm md:text-base text-gray-300 font-medium" data-id="element-66">
                <Check className="w-4 h-4 text-teal-500" data-id="element-67" /> 100% Legal.
              </span>
              <span className="flex items-center gap-2 text-sm md:text-base text-gray-300 font-medium" data-id="element-68">
                <Check className="w-4 h-4 text-teal-500" data-id="element-69" /> 100% Practical.
              </span>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div variants={itemVariants} className="flex flex-col items-start gap-4" data-id="element-70">
            <motion.a href="https://gr-16052026.myshopify.com/cart/45093818040374:1" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} animate={{
            boxShadow: ['0px 0px 0px 0px rgba(20,184,166,0)', '0px 0px 20px 2px rgba(20,184,166,0.3)', '0px 0px 0px 0px rgba(20,184,166,0)']
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="group relative inline-flex items-center justify-center px-8 py-5 text-lg md:text-xl font-bold text-white bg-gradient-to-r from-teal-600 to-teal-500 rounded border border-teal-400/50 overflow-hidden transition-all hover:from-teal-500 hover:to-teal-400 no-underline cursor-pointer" data-id="element-71">
              <div className="absolute inset-0 w-full h-full bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.2)_50%,transparent_75%)] bg-[length:250%_250%,100%_100%] bg-[position:200%_0,0_0] bg-no-repeat transition-all duration-1000 ease-out group-hover:bg-[position:-200%_0,0_0]" data-id="element-72" />

              <span className="relative flex items-center gap-3 tracking-wide" data-id="element-73">
                GET INSTANT ACCESS – $47{' '}
                <span className="text-2xl leading-none" data-id="element-74">✅</span>
              </span>
            </motion.a>

            <div className="flex items-center gap-2 text-xs text-gray-500 font-mono uppercase tracking-widest mt-2" data-id="element-75">
              <div className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" data-id="element-76" />
              Secure Encrypted Connection Established
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Decorative UI Elements */}
      <div className="absolute bottom-8 right-8 hidden md:flex flex-col items-end gap-1 text-teal-500/30 font-mono text-xs" data-id="element-77">
        <span data-id="element-78">SYS.OP.NORMAL</span>
        <span data-id="element-79">LAT: 34.0522° N</span>
        <span data-id="element-80">LNG: 118.2437° W</span>
      </div>
      <div className="absolute top-8 right-8 hidden md:block w-16 h-16 border-t-2 border-r-2 border-teal-500/20" data-id="element-81" />
      <div className="absolute bottom-8 left-8 hidden md:block w-16 h-16 border-b-2 border-l-2 border-teal-500/20" data-id="element-82" />
    </section>;
}